import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { preventDefault, stopPropagation } from '../../../utils/events'
import { DatePicker } from '../DatePicker'
import { DatePickerRangeToggle } from '../DatePickerRangeToggle'
import { DateRangeInput } from '../DateRangeInput'

import { useDatePickerWithRange } from './DatePickerWithRange.hooks'

function DatePickerWithRange({
  Input = DateRangeInput,
  endValue,
  initialIsRange,
  placeholder,
  selectProps,
  startValue,
  value,
  onChange,
  onChangeRange,
  ...restProps
}) {
  const { t } = useTranslation()

  const {
    calendarEndValue,
    calendarStartValue,
    calendarValue,
    inputEnd,
    inputSingle,
    inputStart,
    isPickingEnd,
    isRange,
    setIsRange,
    toTz,
  } = useDatePickerWithRange({ endValue, initialIsRange, startValue, value })

  const [rangeHoverEnd, setRangeHoverEnd] = useState(null)

  function toggleRange(e) {
    stopPropagation(e)
    preventDefault(e)

    const value = calendarStartValue ?? calendarValue

    if (!isRange) {
      // switching SINGLE -> RANGE
      if (value && startValue == null && endValue == null) {
        onChangeRange?.([toTz(value), null])
      }

      setIsRange(true)

      return
    }

    // switching RANGE -> SINGLE
    const singleValue = value ? toTz(value) : null

    onChange?.(singleValue)
    onChangeRange?.([singleValue, null])
    setRangeHoverEnd(null)
    setIsRange(false)
  }

  function handleCalendarSelect(nextValue) {
    if (!nextValue) {
      return
    }

    if (!isRange) {
      onChange?.(toTz(nextValue))

      return
    }

    if (!onChangeRange) {
      return
    }

    if (!calendarStartValue || calendarEndValue) {
      onChangeRange([toTz(nextValue), null])
      setRangeHoverEnd(null)

      return
    }

    const [start, end] =
      calendarStartValue <= nextValue
        ? [calendarStartValue, nextValue]
        : [nextValue, calendarStartValue]

    onChangeRange([toTz(start), toTz(end)])
    setRangeHoverEnd(null)
  }

  function shouldCloseOnSelect(nextClicked) {
    return !isRange || (isPickingEnd && nextClicked != null)
  }

  const dateSelectProps = {
    ...selectProps,
    children: (
      <>
        {selectProps?.children}
        <DatePickerRangeToggle isRange={isRange} onClick={toggleRange} />
      </>
    ),
    optionsProps: {
      rangeEnd: calendarEndValue,
      rangeHoverEnd: isPickingEnd ? rangeHoverEnd : null,
      rangeStart: calendarStartValue,
      onRangeHoverEndChange: setRangeHoverEnd,
    },
  }

  const inputProps = {
    endPlaceholder: t('Common.DatePickerEndDateKey'),
    endValue: inputEnd,
    isRange,
    singleValue: inputSingle,
    startPlaceholder: placeholder || t('Common.DatePickerStartDateKey'),
    startValue: inputStart,
    onChangeRange,
    onChangeSingle: onChange,
  }

  return (
    <DatePicker
      {...restProps}
      {...inputProps}
      Input={Input}
      selectProps={dateSelectProps}
      shouldCloseOnSelect={shouldCloseOnSelect}
      value={calendarValue}
      onChange={handleCalendarSelect}
    />
  )
}

export default DatePickerWithRange
